import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Post } from '@/types'

import Link from 'next/link'

import { Seo, Layout } from '@/components/generals'

import { query } from '@/lib/db'

type PostDetail = Omit<Post, 'published_on'> & { published_label: string }
type RelatedPost = Omit<Post, 'body' | 'published_on'> & { published_label: string }

interface BlogPostProps {
    post: PostDetail
    related?: RelatedPost[]
}

const BlogPost: FC<BlogPostProps> = ({ post, related = [] }): ReactNode => {
    return (
        <Layout>
            <Seo title={post.name} description={post.summary ?? undefined} ogImage={post.main_image ?? undefined} />

            <div className='section'>
                <div className='container'>
                    <div className='blog-details-holder'>
                        <div className='animate-on-load-01'>
                            <div className='blog-category-holder'>
                                <div className='blog-category' data-mdlfy-cms={`posts:${post.id}:category`}>{post.category}</div>
                                <p className='no-margins'>•</p>
                                <p className='no-margins'>{post.published_label}</p>
                            </div>
                        </div>
                        <div className='animate-on-load-02'>
                            <div className='blog-details-title'>
                                <h1 className='white-title-medium' data-mdlfy-cms={`posts:${post.id}:name`}>{post.name}</h1>
                            </div>
                        </div>
                        <div className='animate-on-load-03'>
                            <p className='paragraph-xl' data-mdlfy-cms={`posts:${post.id}:summary`}>{post.summary}</p>
                        </div>
                    </div>
                    <div className='blog-main-image-holder'>
                        <div className='animate-on-load-04 _100-width-and-height'><img src={post.main_image ?? ''} loading='lazy' alt={post.name} className='blog-main-image' /></div>
                    </div>
                    <div className='blog-rich-text-w'>
                        <div className='blog-rich-text-holder'>
                            <div className='social-media-content-blog'>
                                <a href='https://linkedin.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-03.svg' loading='lazy' alt='لینکدین' className='social-media-icon' /></a>
                                <a href='https://instagram.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-02.svg' loading='lazy' alt='اینستاگرام' className='social-media-icon' /></a>
                                <a href='https://twitter.com' target='_blank' rel='noreferrer' className='social-media-link w-inline-block'><img src='/images/Social-Media-Icon-White-01.svg' loading='lazy' alt='ایکس' className='social-media-icon' /></a>
                            </div>
                            <div className='blog-rich-text-wrapper'>
                                <div className='w-richtext' dangerouslySetInnerHTML={{ __html: post.body ?? '' }} />
                                <div className='blog-cta-wrapper'>
                                    <div className='blog-cta-content-holder'>
                                        <div className='blog-cta-content-container'>
                                            <div className='blog-cta-title'>آمادهٔ ساخت دستگاه خودتان هستید؟</div>
                                            <p>کیت کامل دستگاه CNC یا قطعات جداگانه را سفارش دهید و از انبار تهران تحویل بگیرید.</p>
                                        </div>
                                        <Link href='/contact' className='button w-button'>ثبت سفارش</Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <div className='section'>
                    <div className='container'>
                        <div className='center-title'>
                            <div className='title-holder'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='title'>آموزش‌های دیگر</h2>
                                </div>
                            </div>
                        </div>
                        <div className='blog-grid-wrapper'>
                            <div role='list' className='blog-grid'>
                                {related.map((item) => (
                                    <div key={item.id} role='listitem' className='blog-grid-item'>
                                        <Link href={`/blog/${item.slug}`} className='blog-item-holder w-inline-block'>
                                            <div className='blog-item-thumbnail-holder'><img src={item.thumbnail_image ?? item.main_image ?? ''} loading='lazy' alt={item.name} className='blog-item-thumbnail' /></div>
                                            <div className='blog-content'>
                                                <div className='blog-category-holder'>
                                                    <div className='blog-category' data-mdlfy-cms={`posts:${item.id}:category`}>{item.category}</div>
                                                    <p className='no-margins'>•</p>
                                                    <p className='no-margins'>{item.published_label}</p>
                                                </div>
                                                <div className='blog-small-name' data-mdlfy-cms={`posts:${item.id}:name`}>{item.name}</div>
                                                <div className='blog-summery-holder' data-mdlfy-cms={`posts:${item.id}:summary`}>{item.summary}</div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<BlogPostProps> = async (context) => {
    const slug = context.params?.slug as string

    const rows = await query<Post>(
        'SELECT id, slug, name, summary, body, main_image, thumbnail_image, category, featured, published_on FROM posts WHERE slug = ?1',
        [slug]
    )
    const row = rows[0]

    if (!row) {
        return { notFound: true }
    }

    const post: PostDetail = {
        id: row.id,
        slug: row.slug,
        name: row.name,
        summary: row.summary,
        body: row.body,
        main_image: row.main_image,
        thumbnail_image: row.thumbnail_image,
        category: row.category,
        featured: row.featured,
        published_label: row.published_on
            ? new Date(row.published_on).toLocaleDateString('fa-IR', { month: 'long', day: 'numeric', year: 'numeric' })
            : ''
    }

    const relatedRows = await query<Post>(
        'SELECT id, slug, name, summary, main_image, thumbnail_image, category, featured, published_on FROM posts WHERE slug <> ?1 ORDER BY published_on DESC NULLS LAST LIMIT 3',
        [slug]
    )

    const related: RelatedPost[] = relatedRows.map((item) => ({
        id: item.id,
        slug: item.slug,
        name: item.name,
        summary: item.summary,
        main_image: item.main_image,
        thumbnail_image: item.thumbnail_image,
        category: item.category,
        featured: item.featured,
        published_label: item.published_on
            ? new Date(item.published_on).toLocaleDateString('fa-IR', { month: 'long', day: 'numeric', year: 'numeric' })
            : ''
    }))

    return { props: { post, related } }
}

export default BlogPost
